import React, { useEffect, useRef } from 'react';
import {
  GoogleMap,
  withScriptjs,
  withGoogleMap,
  Marker,
  Polyline
} from 'react-google-maps';
import Card from '@mui/material/Card';
import iconMap from 'assets/images/driverImage.png';
import dropPin from 'assets/images/dropPin.png';
import originPin from 'assets/images/originPin.png';

const CommonMap = ({ paths, stops, driverPathDetails, status }) => {
  // Extract driver location details
  const driverLat = driverPathDetails?.location?.lat;
  const driverLng = driverPathDetails?.location?.lng;
  const polyLines = driverPathDetails?.location?.polylines?.trip?.points;

  // Format driver location for marker
  const driverData = driverLat && driverLng ? [{ lat: driverLat, lng: driverLng }] : [];

  // Custom icon configuration for driver marker
  const driverIcon = {
    url: iconMap,
    scaledSize: new window.google.maps.Size(40, 40),
    anchor: new window.google.maps.Point(20, 20),
    scale: 0.7,
  };

  const mapRef = useRef(null);

  // Function to fit map bounds to include all relevant points
  const fitBounds = () => {
    if (!mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    
    // Add stops to bounds
    if (stops?.length) {
      stops.forEach(stop => {
        if (stop?.lat && stop?.lng) {
          bounds.extend(new window.google.maps.LatLng(stop.lat, stop.lng));
        }
      });
    }

    // Add driver location to bounds if available
    if (driverLat && driverLng) {
      bounds.extend(new window.google.maps.LatLng(driverLat, driverLng));
    }

    // Add path points to bounds
    if (paths?.length) {
      paths.forEach(point => {
        if (point?.lat && point?.lng) {
          bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        }
      });
    }

    // Only fit bounds if we have points
    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
    }
  };

  // Fit bounds on mount and when relevant props change
  useEffect(() => {
    fitBounds();
  }, [paths, stops, driverLat, driverLng]);

  // Calculate default center from first path point or first stop
  const defaultCenter = paths?.length > 0 
    ? { lat: paths[0].lat, lng: paths[0].lng }
    : stops?.length > 0 
      ? { lat: stops[0].lat, lng: stops[0].lng }
      : { lat: 12.9716, lng: 77.5946 }; // Default to Bangalore coordinates

  return (
    <Card variant="outlined">
      <div className='gMapCont'>
        <GoogleMap
          ref={mapRef}
          defaultZoom={14}
          defaultCenter={defaultCenter}
        >
          {/* Route Polyline */}
          <Polyline
            path={polyLines || paths}
            options={{
              strokeColor: "#0F538D",
              strokeWeight: 6,
              defaultVisible: true,
            }}
          />

          {/* Destination Marker */}
          {(status?.toLowerCase() === 'ongoing' || status?.toLowerCase() === 'dispatched') && (
            <Marker
              icon={dropPin}
              position={
                status?.toLowerCase() === 'ongoing' && polyLines
                  ? polyLines[polyLines.length - 1]
                  : { lat: stops[1]?.lat, lng: stops[1]?.lng }
              }
            />
          )}

          {/* Driver Marker */}
          {driverData?.length > 0 && driverData[0]?.lat && (
            <Marker
              icon={driverIcon}
              position={driverData[0]}
            />
          )}

          {/* Origin Marker - only show for dispatched status */}
          {status?.toLowerCase() === 'dispatched' && stops?.[0] && (
            <Marker
              icon={originPin}
              position={stops[0]}
            />
          )}
        </GoogleMap>
      </div>
    </Card>
  );
};

// Error boundary wrapper component
const MapErrorBoundary = (props) => {
  try {
    return <CommonMap {...props} />;
  } catch (error) {
    console.error('Error rendering map:', error);
    return (
      <Card variant="outlined">
        <div className="gMapCont">
          <div className="map-error">
            Unable to load map. Please try again later.
          </div>
        </div>
      </Card>
    );
  }
};

export default withScriptjs(
  withGoogleMap(
    MapErrorBoundary
  )
);